import { showUserAlert } from "../utils/userErrorMessage";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Component/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { supabase } from "../supabase/client";

const getToday = () => new Date().toISOString().split("T")[0];
const HOURS = Array.from({ length: 25 }, (_, index) => index);
const RESERVATION_TYPES = [
  { label: "일반", value: "general" },
  { label: "학단", value: "school" },
  { label: "통근", value: "commute" },
  { label: "투어", value: "tour" },
];

export default function OffBusCheck() {
  const navigate = useNavigate();
  const [serviceDate, setServiceDate] = useState(getToday());
  const [carKeyword, setCarKeyword] = useState("");
  const [driverKeyword, setDriverKeyword] = useState("");
  const [cars, setCars] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedType, setSelectedType] = useState("general");

  const schedulesByCar = useMemo(() => {
    const map = new Map();

    reservations.forEach((reservation) => {
      const carNumber = normalizeCarNumber(reservation.car_number);
      const carNumberKey = getCarNumberKey(reservation.car_number);
      const hours = getScheduledHours(reservation.time, reservation.arrival_time);
      const routeName = (reservation.user || "").trim() || "사용자/노선명 없음";

      if (!carNumber || hours.length === 0) return;
      if (!map.has(carNumber)) map.set(carNumber, new Map());
      hours.forEach((hour) => addScheduleRoute(map.get(carNumber), hour, routeName));

      if (carNumberKey && carNumberKey !== carNumber) {
        if (!map.has(carNumberKey)) map.set(carNumberKey, new Map());
        hours.forEach((hour) => addScheduleRoute(map.get(carNumberKey), hour, routeName));
      }
    });

    return map;
  }, [reservations]);

  const handleSearch = async () => {
    const company = localStorage.getItem("email");

    if (!company) {
      showUserAlert("회사 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    if (!serviceDate) {
      showUserAlert("운행일을 선택해 주세요.");
      return;
    }

    setLoading(true);
    setSearched(true);
    setSelectedSlot(null);

    let carsQuery = supabase
      .from("cars")
      .select("car_number_only, driver, direct")
      .eq("company", company)
      .eq("direct", true)
      .order("car_number_only", { ascending: true });

    if (carKeyword.trim()) {
      carsQuery = carsQuery.ilike("car_number_only", `%${carKeyword.trim()}%`);
    }

    if (driverKeyword.trim()) {
      carsQuery = carsQuery.ilike("driver", `%${driverKeyword.trim()}%`);
    }

    const reservationsQuery = supabase
      .from("reservations")
      .select("id, date, time, arrival_time, car_number, user")
      .eq("company", company)
      .eq("date", serviceDate);

    const [carsResult, reservationsResult] = await Promise.all([
      carsQuery,
      reservationsQuery,
    ]);

    setLoading(false);

    if (carsResult.error) {
      showUserAlert(`차량 정보를 불러오지 못했습니다: ${carsResult.error.message}`);
      setCars([]);
      return;
    }

    if (reservationsResult.error) {
      showUserAlert(`예약 정보를 불러오지 못했습니다: ${reservationsResult.error.message}`);
      setReservations([]);
      return;
    }

    setCars(carsResult.data || []);
    setReservations(reservationsResult.data || []);
  };

  const getScheduleRoutes = (carNumber, hour) => {
    const normalized = normalizeCarNumber(carNumber);
    const carNumberKey = getCarNumberKey(carNumber);

    const routes = [
      ...(schedulesByCar.get(normalized)?.get(hour) || []),
      ...(schedulesByCar.get(carNumberKey)?.get(hour) || []),
    ];

    return Array.from(new Set(routes));
  };

  const handleSlotClick = (car, hour, scheduled) => {
    if (scheduled) return;

    setSelectedSlot({
      carNumber: car.car_number_only || "",
      driver: car.driver || "",
      hour,
      date: serviceDate,
    });
  };

  const isSelectedSlot = (carNumber, hour) => {
    if (!selectedSlot) return false;
    return selectedSlot.carNumber === carNumber && selectedSlot.hour === hour;
  };

  const handleCancelReservation = () => {
    setSelectedSlot(null);
    setSelectedType("general");
  };

  const handleReserve = () => {
    if (!selectedSlot) return;

    navigate("/reservation-apply", {
      state: {
        type: selectedType,
        prefill: {
          date: selectedSlot.date,
          time: `${String(selectedSlot.hour).padStart(2, "0")}:00`,
          car_number: selectedSlot.carNumber,
        },
      },
    });
  };

  return (
    <div>
      <Header />
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">휴무 버스 확인</h2>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-sm">운행일:</span>
          <Input
            type="date"
            value={serviceDate}
            onChange={(event) => setServiceDate(event.target.value)}
            className="w-40"
          />
          <Input
            value={carKeyword}
            onChange={(event) => setCarKeyword(event.target.value)}
            placeholder="차량"
            className="w-40"
          />
          <Input
            value={driverKeyword}
            onChange={(event) => setDriverKeyword(event.target.value)}
            placeholder="기사"
            className="w-40"
          />
          <Button onClick={handleSearch} disabled={loading}>
            {loading ? "검색 중..." : "검색"}
          </Button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-4 text-sm text-gray-700">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-4 w-4 rounded-sm bg-red-500 border border-red-600" />
            일정 있음
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-4 w-4 rounded-sm bg-blue-500 border border-blue-600" />
            일정 없음
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-4 w-4 rounded-sm bg-green-500 border border-green-600" />
            선택한 시간
          </span>
        </div>

        <div className="max-h-[70vh] overflow-auto border rounded">
          <table className="min-w-[1250px] w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr>
                <th className="sticky left-0 z-20 bg-gray-100 border p-2 min-w-[110px]">
                  차량
                </th>
                {HOURS.map((hour) => (
                  <th key={hour} className="border p-2 min-w-[42px] text-center">
                    {String(hour).padStart(2, "0")}시
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cars.map((car) => (
                <tr key={`${car.car_number_only}-${car.driver || ""}`}>
                  <th className="sticky left-0 z-10 bg-white border p-2 text-left font-medium">
                    <div>{car.car_number_only || "-"}</div>
                    <div className="text-xs text-gray-500">{car.driver || ""}</div>
                  </th>
                  {HOURS.map((hour) => {
                    const scheduleRoutes = getScheduleRoutes(car.car_number_only, hour);
                    const scheduled = scheduleRoutes.length > 0;
                    const selected = isSelectedSlot(car.car_number_only, hour);
                    return (
                      <td
                        key={`${car.car_number_only}-${hour}`}
                        onClick={() => handleSlotClick(car, hour, scheduled)}
                        className={`border h-8 text-center ${
                          selected ? "bg-green-500" : scheduled ? "bg-red-500" : "bg-blue-500"
                        } ${
                          scheduled
                            ? "cursor-not-allowed"
                            : "cursor-pointer hover:ring-2 hover:ring-green-300 hover:ring-inset"
                        }`}
                        title={selected ? "선택한 시간" : scheduled ? scheduleRoutes.join(", ") : "일정 없음"}
                      />
                    );
                  })}
                </tr>
              ))}
              {cars.length === 0 && (
                <tr>
                  <td colSpan={HOURS.length + 1} className="border p-6 text-center text-gray-500">
                    {searched ? "검색된 차량이 없습니다." : "운행일을 선택하고 검색해 주세요."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSlot && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded bg-white p-5 shadow-lg">
            <p className="mb-4 font-medium">
              해당 일자의 해당 시간에 해당 버스 번호로 예약 하시겠습니까? 어떤 예약을
              진행하시겠습니까?
            </p>
            <div className="mb-4 rounded border bg-gray-50 p-3 text-sm text-gray-700">
              <div>운행일: {selectedSlot.date}</div>
              <div>시간: {String(selectedSlot.hour).padStart(2, "0")}시</div>
              <div>버스 번호: {selectedSlot.carNumber}</div>
            </div>
            <div className="mb-5 grid grid-cols-4 gap-2">
              {RESERVATION_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setSelectedType(type.value)}
                  className={`rounded border px-3 py-2 text-sm ${
                    selectedType === type.value
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelReservation}
                className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100"
              >
                취소
              </button>
              <Button type="button" onClick={handleReserve}>
                예약
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function normalizeCarNumber(value = "") {
  return String(value || "").replace(/\s/g, "").trim();
}

function getCarNumberKey(value = "") {
  const digits = normalizeCarNumber(value).replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : normalizeCarNumber(value);
}

function addScheduleRoute(hourMap, hour, routeName) {
  if (!hourMap.has(hour)) hourMap.set(hour, []);
  hourMap.get(hour).push(routeName);
}

function timeToMinutes(value = "") {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

function getScheduledHours(startValue = "", arrivalValue = "") {
  const start = timeToMinutes(startValue);
  if (start === null) return [];

  const arrival = timeToMinutes(arrivalValue);
  if (arrival === null) return [Math.floor(start / 60)];

  const end = arrival <= start ? arrival + 24 * 60 : arrival;
  const hours = [];

  for (let hour = 0; hour <= 24; hour += 1) {
    const hourStart = hour * 60;
    const hourEnd = hour === 24 ? 24 * 60 + 1 : hourStart + 60;
    if (start < hourEnd && hourStart < end) hours.push(hour);
  }

  return hours;
}
